<template>
  <div class="cart-drawer critical-component-hide">
    <p class="cart-drawer__header">
      <span
        class="
          cart-drawer__item-count
          text-body-s-regular
        "
        v-text="$string(
          'cart.item_count',
          {
            pluralise: cart.item_count,
            replace: { count: cart.item_count },
          },
        )"
      />

      <span
        class="
          cart-drawer__total-price
          text-body-s-regular
        "
        v-text="$formatMoney(cart.total_price)"
      />
    </p>

    <form
      v-if="cart.item_count > 0 || cartStatus.label === 'adding'"
      class="cart-drawer__form"
      :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
      method="post"
    >
      <div
        class="cart-drawer__line-items"
        js-overlay="scrollElement"
      >
        <template
          v-for="(lineItem, index) of cartItems"
          :key="getCartItemKey(lineItem)"
        >
          <line-item
            class="cart-drawer__line-item"
            :line-item="lineItem"
            small
          />

          <divider-line v-if="(index + 1) !== cartItems.length" />
        </template>
      </div>

      <cart-summary
        v-if="cart.item_count > 0"
        class="cart-drawer__footer"
        small
        cart-drawer
        :tax-message="taxMessage"
      />
    </form>

    <p
      v-else
      class="
        cart-drawer__empty
        text-body-s-regular
      "
      v-text="$string('cart.empty.drawer_text')"
    />
  </div>
</template>

<script>

/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart.
 *
 * @param {Object} overlay - Overlay API.
 *
 */
import { mapState } from 'vuex'

import CartSummary from '~async/cart-summary/cart-summary'
import DividerLine from '~async/utils/divider-line/divider-line'
import LineItem from '~async/line-item/line-item'

export default {
  name: 'CartDrawer',

  components: {
    CartSummary,
    DividerLine,
    LineItem,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    taxMessage: {
      type: String,
      default: '',
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.cart.items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.cart.items,
      ]
    },
  },

  watch: {

    /**
     * Watch cart items to update focus trap.
     */
    'cart.items'() {
      if (!this.overlay.isActive) {
        return
      }

      this.overlay.update({ currentElement: document.activeElement })
    },
  },

  mounted() {
    if (!this.overlay.isActive) {
      return
    }

    this.overlay.update()
  },

  methods: {

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @param {Number} index - Line item index.
     * @returns {String}
     */
    getCartItemKey(item, index) {
      if (!item) {
        return `cart-line-item-loading-${index}`
      }

      return `cart-line-item-${item.key}`
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.overlay.open({
        component: 'cart-drawer',
        ignoreDismissed: true,
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.overlay.close()
    },
  },
}
</script>

<style lang="scss">
@import './cart-drawer';
</style>
